import {
  downloadAction,
  printAction,
  skipGAEventAction,
  uploadAction,
} from '../../../../utils/actions'
import { COUNTRY } from '../../../../utils/constants'
import { GAService } from '../../../../utils/services'
import {
  APP_CONFIG,
  HOLDOWN_LABELS,
  POST_BASE_LABELS,
  POST_BASE_MATERIAL_TYPE,
  POST_BASE_SUBTYPE,
} from '../../common/constants'
import { migrateJSONUpload } from '../../common/fns'
import { formatCommonParameters } from '../../common/formatCommonParameters'
import { handleDataLayerEvent } from '../../common/googleAnalytics'
import { postCalculation } from '../actions/calculation.action'
import { selectInputState } from '../slices/input.slice'
import { selectOutputState } from '../slices/output.slice'

const handleCanadaGAInput = (input, formatGAInput) => {
  let gaInput = {}
  let commonGAInputs = {
    settings_country: formatGAInput.settingsCountry,
    upliftDuration: formatGAInput.upliftDuration,
    factoredUpliftLRFD: formatGAInput.factoredUpliftLRFD,
    corrosionResistance: formatGAInput.corrosionResistance,
    post_width: formatGAInput.postWidth,
    post_thickness: formatGAInput.postThickness,
    stemwallThickness: formatGAInput.stemwallThickness,
    showAllModels: true,
  }

  if (input.holdownDesign) {
    gaInput = {
      ...commonGAInputs,
      condition: HOLDOWN_LABELS[COUNTRY.CANADA],
      loadType: formatGAInput.loadType,
      holdownType: formatGAInput.holdownType,
      holdownFastenersType: formatGAInput.holdownFastenersType,
      installationType: formatGAInput.installationType,
      lumberSpecies: formatGAInput.lumberSpecies,
    }
  }

  if (input.postBaseDesign) {
    gaInput = {
      ...commonGAInputs,
      condition: POST_BASE_LABELS[COUNTRY.CANADA],
      downloadDuration: formatGAInput.downloadDuration,
      factoredDownloadLRFD: formatGAInput.factoredDownloadLRFD,
      lateralF1: formatGAInput.lateralF1,
      lateralF2: formatGAInput.lateralF2,
      postBaseType: formatGAInput.postBaseType,
      postBaseFastenersType: formatGAInput.postBaseFastenersType,
      foundationType: formatGAInput.foundationType,
      lumberSpecies: formatGAInput.postBaseLumberSpecies,
    }
  }

  return gaInput
}

const handlePostBaseGAInput = (input, formatGAInput) => {
  let gaInput = {}
  let commonGAInputs = {
    settings_country: formatGAInput.settingsCountry,
    condition: POST_BASE_LABELS[COUNTRY.USA],
    showAllModels: true,
    upliftLoadASD: formatGAInput.upliftLoadASD,
    lateralF1: formatGAInput.lateralF1,
    postBaseSubType: formatGAInput.postBaseSubType,
    postBaseFastenersType: formatGAInput.postBaseFastenersType,
    lumberSpecies: formatGAInput.postBaseLumberSpecies,
    materialType: formatGAInput.postBaseMaterialType,
    corrosionResistance: formatGAInput.corrosionResistance,
  }

  if (input.postBaseSubType === POST_BASE_SUBTYPE.POST_INSTALLED) {
    if (input.postBaseMaterialType === POST_BASE_MATERIAL_TYPE.CONCRETE) {
      gaInput = {
        ...commonGAInputs,
        downloadASD: formatGAInput.downloadASD,
        lateralF2: formatGAInput.lateralF2,
      }
    } else {
      gaInput = commonGAInputs
    }
  } else {
    gaInput = {
      ...commonGAInputs,
      downloadASD: formatGAInput.downloadASD,
      lateralF2: formatGAInput.lateralF2,
      loadType: formatGAInput.postBaseLoadType,
      foundationType: formatGAInput.postBaseGeometryType,
      anchorageApplication: formatGAInput.postBaseGeometrySubType,
    }
  }

  return gaInput
}

export const generateGAInput = (appInput) => {
  let cloneAppInput = Object.assign({}, appInput)
  let formatGaInput = formatCommonParameters(cloneAppInput)
  let gaInput = {}

  if (cloneAppInput.settings.country === COUNTRY.CANADA) {
    gaInput = handleCanadaGAInput(cloneAppInput, formatGaInput)
  } else {
    if (cloneAppInput.postBaseDesign) {
      gaInput = handlePostBaseGAInput(cloneAppInput, formatGaInput)
    } else if (cloneAppInput.holdownDesign && !cloneAppInput.foundationDesign) {
      gaInput = {
        settings_country: formatGaInput.settingsCountry,
        condition: HOLDOWN_LABELS[COUNTRY.USA],
        upliftLoadASD: formatGaInput.upliftLoadASD,
        holdownSubType: formatGaInput.holdownSubType,
        holdownFasteners: formatGaInput.holdownFastenersType,
        lumberSpecies: formatGaInput.lumberSpecies,
        wallThickness: formatGaInput.wallThickness,
        showAllModels: true,
      }
    } else if (cloneAppInput.foundationDesign && !cloneAppInput.holdownDesign) {
      gaInput = {
        settings_country: formatGaInput.settingsCountry,
        condition: 'Anchorage',
        upliftLoadASD: formatGaInput.upliftLoadASD,
        loadType: formatGaInput.loadType,
        materialType: formatGaInput.materialType,
        compressiveStrength: formatGaInput.compressiveStrength,
        anchorType: formatGaInput.anchorageOption,
        foundationType: formatGaInput.geometryType,
        anchorageApplication: formatGaInput.geometrySubType,
        showAllModels: true,
      }
    } else {
      gaInput = {
        settings_country: formatGaInput.settingsCountry,
        condition: 'both',
        upliftLoadASD: formatGaInput.upliftLoadASD,
        loadType: formatGaInput.loadType,
        holdownSubType: formatGaInput.holdownSubType,
        holdownFasteners: formatGaInput.holdownFastenersType,
        lumberSpecies: formatGaInput.lumberSpecies,
        wallThickness: formatGaInput.wallThickness,
        materialType: formatGaInput.materialType,
        compressiveStrength: formatGaInput.compressiveStrength,
        anchorType: formatGaInput.anchorageOption,
        foundationType: formatGaInput.geometryType,
        anchorageApplication: formatGaInput.geometrySubType,
        showAllModels: true,
        installOverSheathing: formatGaInput.installOverSheathing,
        raisedFloor: formatGaInput.raisedFloor,
      }
    }
  }
  return gaInput
}

const gaEvents = (store) => (next) => (action) => {
  next(action)

  const state = store.getState()
  const input = selectInputState(state)
  const output = selectOutputState(state)

  switch (action.type) {
    case postCalculation.rejected.type:
    case postCalculation.fulfilled.type: {
      if (output.canSkipGAEvent) {
        // restore default value because we only skip once
        store.dispatch(skipGAEventAction(false))
        break
      } else if (output.status.msgLevel !== 'error') {
        handleDataLayerEvent({
          appAction: 'Calculation_Action',
          appInputs: generateGAInput(input),
          appOutputs: output,
        })
      }
      break
    }
    case uploadAction.type: {
      let payload = action.payload
      const inputState = selectInputState(payload)

      if (payload.appVersion < APP_CONFIG.appVersion) {
        payload = migrateJSONUpload(inputState)
      } else {
        payload = inputState
      }

      handleDataLayerEvent({
        appAction: GAService.APP_ACTION.UPLOAD,
        appInputs: generateGAInput(payload),
        appOutputs: output,
      })
      store.dispatch(skipGAEventAction(true))
      break
    }
    case downloadAction.type: {
      handleDataLayerEvent({
        appAction: GAService.APP_ACTION.DOWNLOAD,
        appInputs: generateGAInput(input),
        appOutputs: output,
      })
      break
    }
    case printAction.type: {
      handleDataLayerEvent({
        appAction: GAService.APP_ACTION.PRINT,
        appInputs: generateGAInput(input),
        appOutputs: output,
      })
      break
    }
    default:
      break
  }
}

export default gaEvents
